import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Instagram, Twitter, TrendingUp, Heart, Share2, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SocialMediaPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [socialPosts, setSocialPosts] = useState([]);
  const [trendingHashtags, setTrendingHashtags] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchSocialContent();
    fetchTrendingHashtags();
  }, []);

  const fetchSocialContent = async () => {
    try {
      const response = await axios.get(`${API}/social/content?limit=50`);
      setSocialPosts(response.data.posts || []);
    } catch (error) {
      console.error('Error fetching social content:', error);
    }
  };

  const fetchTrendingHashtags = async () => {
    try {
      const response = await axios.get(`${API}/social/trending-hashtags`);
      setTrendingHashtags(response.data.hashtags || []);
    } catch (error) {
      console.error('Error fetching hashtags:', error);
    }
  };

  const likePost = async (postId) => {
    try {
      await axios.post(`${API}/social/${postId}/like`);
      fetchSocialContent();
    } catch (error) {
      toast.error('Failed to like post');
    }
  };

  return (
    <div className="min-h-screen" style={{ 
      background: theme === 'dark' 
        ? 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)' 
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      <div className={`backdrop-blur-md ${theme === 'dark' ? 'bg-white/5' : 'bg-black/5'} border-b ${theme === 'dark' ? 'border-white/10' : 'border-black/10'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button 
            onClick={() => navigate('/')} 
            className={`flex items-center ${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors mb-4`}
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </button>
          <h1 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Cricket Social Media</h1>
          <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-2`}>Best memes, posts, and reels from across the cricket world</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Trending Sidebar */}
          <div className="space-y-6">
            <Card className={`glass-card ${theme === 'light' ? 'bg-white/95' : ''}`}>
              <div className="flex items-center space-x-2 mb-4">
                <TrendingUp className="w-5 h-5 text-orange-500" />
                <h3 className={`font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Trending</h3>
              </div>
              <div className="space-y-2">
                {trendingHashtags.slice(0, 10).map((tag, idx) => (
                  <button
                    key={`${tag}-${idx}`}
                    className={`w-full text-left px-3 py-2 rounded-lg ${theme === 'dark' ? 'hover:bg-white/10 text-gray-300' : 'hover:bg-gray-100 text-gray-700'} transition-colors text-sm`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </Card>
          </div>

          {/* Main Feed */}
          <div className="lg:col-span-3 space-y-6">
            <Tabs defaultValue="all" className="w-full">
              <TabsList className={theme === 'dark' ? 'bg-white/10' : 'bg-gray-200'}>
                <TabsTrigger value="all">All Posts</TabsTrigger>
                <TabsTrigger value="memes">Memes</TabsTrigger>
                <TabsTrigger value="videos">Videos</TabsTrigger>
                <TabsTrigger value="news">News</TabsTrigger>
              </TabsList>

              <TabsContent value="all" className="space-y-4 mt-6">
                {socialPosts.map((post) => (
                  <Card key={post.id} className={`glass-card ${theme === 'light' ? 'bg-white/95' : ''}`}>
                    <div className="space-y-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center">
                            <Twitter className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h4 className={`font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{post.author || 'Cricket Fan'}</h4>
                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>@cricketlover</p>
                          </div>
                        </div>
                      </div>
                      
                      <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>{post.content}</p>
                      
                      {post.image_url && (
                        <div className="rounded-lg overflow-hidden">
                          <img src={post.image_url} alt="Post" className="w-full" />
                        </div>
                      )}
                      
                      <div className="flex items-center space-x-6 text-sm">
                        <button
                          onClick={() => likePost(post.id)}
                          className={`flex items-center space-x-1 ${theme === 'dark' ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'} transition-colors`}
                        >
                          <Heart className="w-5 h-5" />
                          <span>{post.likes || 0}</span>
                        </button>
                        <button className={`flex items-center space-x-1 ${theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-700'} transition-colors`}>
                          <Share2 className="w-5 h-5" />
                          <span>Share</span>
                        </button>
                      </div>
                    </div>
                  </Card>
                ))}
                
                {socialPosts.length === 0 && (
                  <div className="text-center py-16">
                    <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} text-lg`}>Loading cricket social content...</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}
